import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../useApi';
import type { Subscription } from '../api';
import { ErrorNotice } from '../components/ErrorNotice';
import { MarketingPreference } from '../components/MarketingPreference';
import { stripQueryKeys } from '../stripQueryKeys';

// Plan selection and subscription state.
//
// This screen is also where Stripe lands the customer on the way back from
// Checkout and from the billing portal — every success_url, cancel_url and
// return_url in app/api/src/functions/billing.ts points here. Before it
// existed those redirects fell through the SPA's catch-all route to the tenant
// list, so paying produced no confirmation of any kind.
//
// Nothing about money is computed here on purpose. The price lives in the
// Stripe Price object and the discount in whatever coupon the customer
// redeemed, so anything this page rendered from its own arithmetic would be a
// second source of truth that could disagree with the invoice.

// ⚠ THE ENTITLED-STATUS LIST USED TO LIVE HERE, mirroring ENTITLED_STATUSES in
// the API. Two copies of a money rule is one copy too many: changing
// entitlement policy on the server would have left this page still calling a
// checkout "registered" on the old rule, or never calling it registered at
// all. The server now sends `entitled` as a decided answer — see
// billing/entitlement.ts — and this page never sees a Stripe status.

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Trial',
  past_due: 'Payment overdue',
  canceled: 'Cancelled',
  unpaid: 'Unpaid',
  incomplete: 'Awaiting payment',
  incomplete_expired: 'Sign-up expired',
  paused: 'Paused',
};

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function BillingPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const api = useApi();
  const [params, setParams] = useSearchParams();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  /** The server's answer to "may this account write", never re-derived here. */
  const [entitled, setEntitled] = useState(false);
  // From the server, not a literal here: the same number decides whether the
  // admin cancellations report says a refund is owed, and a promise to the
  // customer that disagrees with the report is the worst kind of drift.
  const [refundDays, setRefundDays] = useState(7);
  // Same reasoning: the retention window is published in the Privacy Policy and
  // drives the sweep that actually deletes the data. A literal here would be a
  // third copy of a number that must not disagree with itself.
  const [retentionDays, setRetentionDays] = useState(30);
  const [deletionDate, setDeletionDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stripe redirects back the instant Checkout completes, but the row on our
  // side is written by the webhook, which arrives separately and slightly
  // later. Showing "no subscription" in that window would tell a customer who
  // has just paid that their payment did not register.
  // Read once into state; the flag is then stripped from the URL below.
  const [confirming, setConfirming] = useState(() => params.get('checkout') === 'success');

  // ⚠ Stripped AFTER the poll has been started from it, not before. Left in
  // place, a refresh re-entered the confirming poll — so somebody who had
  // already paid and reloaded got the spinner again, and a shared link
  // re-entered it for whoever opened it. `replace` so back cannot return to
  // the flagged URL and start it a third time.
  //
  // `checkout=canceled` is deliberately NOT stripped here: it has its own
  // Dismiss button, because that notice is the one a customer may want to sit
  // and read rather than have vanish from under them.
  useEffect(() => {
    if (params.get('checkout') !== 'success') return;
    setParams(stripQueryKeys(params, ['checkout']), { replace: true });
  }, [params, setParams]);
  const deadline = useRef(Date.now() + 20_000);

  const load = useCallback(async (): Promise<boolean> => {
    if (!api || !tenantId) return false;
    const result = await api.get<{
      subscription: Subscription | null;
      entitled: boolean;
      refundWindowDays: number;
      dataRetentionDays: number;
      dataDeletionDate: string | null;
    }>(`/tenants/${tenantId}/billing`);
    setSubscription(result.subscription);
    setEntitled(result.entitled);
    if (result.refundWindowDays) setRefundDays(result.refundWindowDays);
    if (result.dataRetentionDays) setRetentionDays(result.dataRetentionDays);
    setDeletionDate(result.dataDeletionDate ?? null);
    // Returns the SERVER's answer, so the confirmation loop below stops on the
    // same rule that decides whether a write would actually be allowed.
    return result.entitled;
  }, [api, tenantId]);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        const loaded = await load();
        if (cancelled) return;

        // Keep asking until the webhook lands, then stop. Bounded, because a
        // webhook that never arrives is a real possibility and an spinner that
        // never resolves tells the customer nothing about what to do next.
        if (confirming && !loaded) {
          if (Date.now() < deadline.current) {
            setTimeout(() => void run(), 2000);
            return;
          }
        }
        if (!cancelled) setConfirming(false);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load billing.');
          setConfirming(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [load, confirming]);

  // Both buttons hand off to a Stripe-hosted page. Card details never reach
  // this app, which is the same instinct as the Acumatica connector: do not
  // handle what you do not have to.
  async function go(path: string, urlKey: 'checkoutUrl' | 'portalUrl') {
    if (!api || !tenantId) return;
    setWorking(true);
    setError(null);
    try {
      const result = await api.post<Partial<Record<typeof urlKey, string>>>(
        `/tenants/${tenantId}/billing/${path}`,
        {}
      );
      const url = result[urlKey];
      if (!url) throw new Error('Stripe returned no URL to continue to.');
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach Stripe.');
      setWorking(false);
    }
  }


  return (
    <>
      <div className="page-header">
        <div>
          <Link to={`/tenants/${tenantId}`} className="muted small">
            ← Instance
          </Link>
          <h1>Billing</h1>
        </div>
      </div>

      {error && <ErrorNotice message={error} />}

      {params.get('checkout') === 'canceled' && (
        <div className="notice warn">
          Checkout was canceled — nothing has been charged. You can pick up where
          you left off whenever you are ready.
          <button
            type="button"
            className="link-button"
            onClick={() => {
              params.delete('checkout');
              setParams(params, { replace: true });
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {confirming && (
        <div className="notice">
          Confirming your subscription with Stripe… this usually takes a few
          seconds.
        </div>
      )}

      {loading && !subscription && <p className="muted">Loading…</p>}

      {!loading && !confirming && !entitled && (
        <div className="card">
          <h2>Choose a plan</h2>
          <p className="muted">
            AcuTidy is billed per production/sandbox pair. Connecting a sandbox
            alongside production costs nothing extra — rehearsing a bulk edit
            before it touches live data is the safe way to use this tool, and
            charging for it would discourage exactly that.
          </p>
          <p className="muted">
            Have a promotion code? There is a field for it on the Stripe checkout
            page.
          </p>
          <button
            type="button"
            className="primary"
            disabled={working || !api}
            onClick={() => void go('checkout', 'checkoutUrl')}
          >
            {working ? 'Opening Stripe…' : 'Continue to payment'}
          </button>
          <p className="muted small">
            You can cancel within {refundDays} days for a full refund. Refunds are
            issued by hand rather than automatically, so allow up to {refundDays}{' '}
            days for the money to appear back on your card.
          </p>
        </div>
      )}

      <MarketingPreference />

      {subscription && (
        <div className="card">
          <h2>Subscription</h2>
          <dl className="detail-grid">
            <dt>Status</dt>
            <dd>{STATUS_LABEL[subscription.status] ?? subscription.status}</dd>

            <dt>Instance pairs</dt>
            <dd>{subscription.quantity}</dd>

            <dt>{subscription.cancelAtPeriodEnd ? 'Access ends' : 'Renews'}</dt>
            <dd>{formatDate(subscription.currentPeriodEnd)}</dd>
          </dl>

          {subscription.status === 'past_due' && (
            <div className="notice warn">
              The last payment did not go through. Writing still works while Stripe
              retries the card — update your payment details to avoid losing access.
            </div>
          )}

          {/*
            The cancellation notice does three jobs, and the second two were
            missing entirely.

            It warns that the data goes. Somebody who cancels, comes back two
            months later and finds nothing was told, not surprised — and
            "within 30 days" is a promise in the Privacy Policy, so they are
            entitled to know when.

            And it offers the way back. A customer who cancelled by mistake, or
            has changed their mind, should not have to work out whether that is
            even possible. Stripe allows renewing right up to the end of the
            paid period, so the honest thing and the commercially sensible
            thing are the same sentence.
          */}
          {subscription.cancelAtPeriodEnd && (
            <div className="notice warn">
              <strong>
                This subscription is set to cancel on{' '}
                {formatDate(subscription.currentPeriodEnd)}.
              </strong>{' '}
              You keep full access until then — nothing changes before that date.
              <br />
              <br />
              Afterwards we delete the credentials connecting AcuTidy to your
              Acumatica straight away, and the rest — your instances, job history
              and undo log — within {retentionDays} days
              {deletionDate ? `, by ${formatDate(deletionDate)}` : ''}.
              Coming back after that means starting from scratch: the undo log for
              past jobs will not be there.
              <br />
              <br />
              <strong>Changed your mind?</strong> You can restart this subscription
              from <em>Manage billing</em> any time before{' '}
              {formatDate(subscription.currentPeriodEnd)} and nothing is lost.
            </div>
          )}

          <button
            type="button"
            className="primary"
            disabled={working || !api}
            onClick={() => void go('portal', 'portalUrl')}
          >
            {working ? 'Opening Stripe…' : 'Manage billing'}
          </button>
          <p className="muted small">
            Invoices, card details and cancellation are all handled on Stripe's own
            page. Cancelling within {refundDays} days of signing up qualifies for a
            full refund — allow up to {refundDays} days for it to be processed.
          </p>
        </div>
      )}
    </>
  );
}
